import React, { lazy, Suspense } from 'react';
import { render, RenderResult } from '@testing-library/react';
import { Navigate, useRoutes, MemoryRouter } from 'react-router-dom';

import { DefaultLayout } from 'src/layouts';
import { PATH_AFTER_LOGIN } from 'src/config-global';

import { SplashScreen } from 'src/components/loading-screen';
import { AuthGuard, GuestGuard } from 'src/auth/guard';
import { useAuthStore } from 'src/auth/auth.store';

import { authRoutes } from './auth';
import { errorRoutes } from './error';

// PAGE_IMPORTS
const WelcomePage = lazy(() => import('src/modules/welcome/welcome'));
const AppHomePage = lazy(() => import('src/modules/app/home'));
const BenefitDetailsPage = lazy(() => import('src/modules/app/benefit-details'));
const RedeemSuccessPage = lazy(() => import('src/modules/app/redeem-success'));
const OffersPage = lazy(() => import('src/modules/app/offers'));
const RedeemsPage = lazy(() => import('src/modules/app/redeems'));

export const renderWithRouter = (ui: React.ReactElement): RenderResult => render(<MemoryRouter>{ui}</MemoryRouter>);

function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <SplashScreen />;
  }

  return <Navigate to={isAuthenticated ? '/app' : '/welcome'} replace />;
}

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            {/* Redirect root based on auth state */}
            <RootRedirect />
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/welcome',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <GuestGuard>
              <WelcomePage />
            </GuestGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <AppHomePage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/benefits/:id',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <BenefitDetailsPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/benefits/:id/success',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <RedeemSuccessPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/offers',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            {/* <AuthGuard> */}
            <OffersPage />
            {/* </AuthGuard> */}
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: '/app/redeems',
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout>
            <AuthGuard>
              <RedeemsPage />
            </AuthGuard>
          </DefaultLayout>
        </Suspense>
      ),
    },
    ...authRoutes,

    // RELATION_PATH_PAGES

    { path: '*', element: <Navigate to="/404" replace /> },

    ...errorRoutes,
  ]);
}
